/**
 * @file Top app header. Role-aware:
 *   • admin     — full tab bar + a search lens + profile menu
 *   • employee  — Your gear (single tab; no tab bar on mobile)
 *
 * Admin extras:
 *   • Search lens (right of the tabs) → flips the header into a full-width
 *     whole-app search bar (logo stays); results across people + devices,
 *     each navigating to its detail page. See search.js.
 *   • Test mode → a checkbox in the profile menu (next to Sign out); both
 *     also live in Settings → Session, which is where they are inside the
 *     IT Hub (the hub hides the profile block).
 */

import { el, clear, debounce }      from './utils.js';
import { Icons }                    from './icons.js';
import { strvLogo }                 from './logo.js';
import { getCurrentUser, isAdmin } from './auth.js';
import { toggleTestMode, signOutHere } from './session-actions.js';
import { getRoute, onRoute, navigate } from './router.js';
import { isTestMode } from './test-mode.js';
import { isViewingAsAdmin } from './view-as.js';
import { runGlobalSearch } from './search.js';
import { CATEGORIES } from './config.js';
import { toast } from './utils.js';
import { avatar } from './standard/js/dom.js';
import { icon } from './standard/js/icons.js';
import { currentTheme, installThemeSwitch } from './standard/js/theme.js';
import { svgFromStandard } from './lib/svg.js';

const ADMIN_TABS = [
  { id: 'me',           label: 'Your gear',    icon: Icons.user     },
  { id: 'people',       label: 'People',       icon: Icons.user     },
  { id: 'onboarding',   label: 'Onboarding',   icon: Icons.plus     },
  { id: 'offboarding',  label: 'Offboarding',  icon: Icons.inbox    },
  { id: 'devices',      label: 'Devices',      icon: Icons.laptop   },
  { id: 'reconcile',    label: 'Reconcile',    icon: Icons.refresh  },
  { id: 'pending',      label: 'Pending Abra', icon: Icons.clock    },
  { id: 'data-quality', label: 'Data quality', icon: Icons.bell     },
  { id: 'audit-log',    label: 'Audit log',    icon: Icons.history  },
  { id: 'settings',     label: 'Settings',     icon: Icons.settings },
];

const EMPLOYEE_TABS = [
  { id: 'me', label: 'Your gear', icon: Icons.user },
];

function tabsFor(adminMode) {
  return adminMode ? ADMIN_TABS : EMPLOYEE_TABS;
}

export function renderHeader(host) {
  clear(host);
  // Effective role — the view-as override (test mode only) can demote the
  // shell to the employee layout while the real token stays admin.
  const adminMode = isAdmin() && isViewingAsAdmin();
  const tabs = tabsFor(adminMode);

  const homeHref = adminMode ? '#/people' : '#/me';

  const tabsEl = el('nav', { class: 'tabs' },
    ...tabs.map((t) =>
      el('a', { class: 'tab', href: `#/${t.id}`, 'data-tab': t.id }, t.label),
    ),
  );

  const profile = renderProfile();
  // Search lens lives where the test toggle used to (test mode moved into
  // the profile menu). Search is admin-only (it indexes admin collections).
  const lens = adminMode ? renderSearchLens() : null;
  const rightChildren = adminMode ? [lens, renderThemeSwitch(), profile] : [renderThemeSwitch(), profile];

  const container = el('div', { class: 'container' },
    el('a', { class: 'logo', href: homeHref, 'aria-label': 'Gear home' }, strvLogo()),
    tabsEl,
    el('div', { class: 'header-right' }, ...rightChildren),
  );
  const header = el('header', { class: 'app-header' }, container);

  // Search overlay: a full-width bar that replaces the nav + right cluster
  // when active (logo stays). Mounted into the same container so it can
  // overlay the row; toggled via the `searching` class on the header.
  if (adminMode) mountSearch(header, container, lens);

  host.appendChild(header);

  // Self-unsubscribing: renderApp() replaces the whole shell on every
  // test-mode / view-as flip, so without this each old header's callback
  // stayed subscribed and kept poking detached DOM for the session.
  const unsubRoute = onRoute((route) => {
    if (!header.isConnected) { unsubRoute(); return; }
    for (const a of tabsEl.querySelectorAll('.tab')) {
      const on = a.dataset.tab === route.section;
      a.classList.toggle('active', on);
      if (on) a.setAttribute('aria-current', 'page'); else a.removeAttribute('aria-current');
    }
  });
}

// ── Theme: dark / light, arcade on 8 fast clicks or the Konami code ────────
// Standalone only — inside the IT Hub the hub decides the theme (CSS hides
// this button when embedded).
let themeSwitchInstalled = null;
function renderThemeSwitch() {
  const btn = el('button', { class: 'icon-btn theme-switch', 'aria-label': 'Switch theme', title: 'Switch theme' });
  const paint = () => btn.replaceChildren(svgFromStandard(icon(currentTheme() === 'light' ? 'moon' : 'sun')));
  paint();
  // The header re-renders on every test-mode / view-as flip; keep exactly one
  // switch (and one Konami listener) alive.
  themeSwitchInstalled?.destroy();
  themeSwitchInstalled = installThemeSwitch({ switchEl: btn, onChange: paint });
  return btn;
}

// ── Whole-app search ───────────────────────────────────────────────────────
function renderSearchLens() {
  return el('button', {
    class: 'icon-btn search-lens',
    'aria-label': 'Search',
    title: 'Search people & devices',
  }, Icons.search());
}

// Wires the search overlay into the header. Clicking the lens flips the
// header into `searching`, revealing an input + results dropdown; Escape
// or the X closes it; picking a result navigates and closes.
function mountSearch(header, container, lens) {
  const input = el('input', {
    type: 'search',
    class: 'search-overlay-input input-serial',
    placeholder: 'Search people, devices, serials, phone numbers…',
    'aria-label': 'Search the whole app',
  });
  const closeBtn = el('button', { class: 'icon-btn', 'aria-label': 'Close search' }, Icons.close());
  const results = el('div', { class: 'search-results', role: 'listbox' });
  const overlay = el('div', { class: 'search-overlay' }, input, closeBtn);
  container.appendChild(overlay);
  container.appendChild(results);

  const close = () => {
    header.classList.remove('searching');
    input.value = '';
    clear(results);
    results.classList.remove('open');
  };
  const open = () => {
    header.classList.add('searching');
    input.focus();
  };

  lens.addEventListener('click', (e) => { e.stopPropagation(); open(); });
  closeBtn.addEventListener('click', close);
  input.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  // Click-away closes (but not clicks inside the overlay/results). The
  // handler self-removes once its header is detached from the DOM (a
  // re-render replaces the header), so listeners don't accumulate across
  // renderApp() calls.
  const onDocClick = (e) => {
    if (!document.body.contains(header)) { document.removeEventListener('click', onDocClick); return; }
    if (!header.classList.contains('searching')) return;
    if (overlay.contains(e.target) || results.contains(e.target) || lens.contains(e.target)) return;
    close();
  };
  document.addEventListener('click', onDocClick);

  const run = debounce(async () => {
    const q = input.value;
    if (q.trim().length < 2) { clear(results); results.classList.remove('open'); return; }
    let res;
    try { res = await runGlobalSearch(q); }
    catch (err) { console.error('[search] failed', err); return; }
    if (input.value !== q) return; // a newer keystroke superseded this one
    renderResults(results, res, close);
  }, 180);
  input.addEventListener('input', run);
}

function renderResults(host, { people, devices, decommissionedCount = 0, query }, onPick) {
  clear(host);
  host.classList.add('open');
  const go = (path) => { onPick(); navigate(path); };
  // A decommissioned-only match must not read as "we never had this device".
  const decomHint = decommissionedCount
    ? (() => {
        const row = el('div', { class: 'search-result', role: 'option' },
          el('span', { class: 'search-result-icon' }, Icons.laptop()),
          el('span', { class: 'search-result-main' },
            `${decommissionedCount} match${decommissionedCount === 1 ? '' : 'es'} in the Decommissioned registry`),
          el('span', { class: 'search-result-sub' }, 'Open Devices → Decommissioned'),
        );
        row.addEventListener('click', () => go('/devices'));
        return row;
      })()
    : null;

  if (!people.length && !devices.length) {
    host.appendChild(el('div', { class: 'search-empty' },
      decommissionedCount
        ? `No active people or devices match “${query}” — but it exists in the Decommissioned registry.`
        : `No people or devices match “${query}”.`));
    if (decomHint) host.appendChild(decomHint);
    return;
  }

  if (people.length) {
    host.appendChild(el('div', { class: 'search-group-label' }, 'People'));
    for (const p of people) {
      host.appendChild(el('button', {
        class: 'search-result', role: 'option',
        onClick: () => go(`/people/${p.id}`),
      },
        el('span', { class: 'search-result-icon' }, Icons.user()),
        el('span', { class: 'search-result-main' }, p.fullName || p.email || p.id),
        // Matched via a SIM number → show THAT number rather than the email,
        // so two people holding the same number read as an obvious duplicate.
        // Returned lines are marked, since "who used to have this?" is a real
        // question when a line is still being billed.
        p.matchedPhone
          ? el('span', { class: 'search-result-sub' },
              p.matchedPhone.raw,
              p.matchedPhone.active ? '' : ' · returned')
          : el('span', { class: 'search-result-sub' }, p.email || p.employeeNumber || ''),
      ));
    }
  }
  if (devices.length) {
    host.appendChild(el('div', { class: 'search-group-label' }, 'Devices'));
    for (const d of devices) {
      const cat = CATEGORIES[d.category]?.label || d.category || 'device';
      host.appendChild(el('button', {
        class: 'search-result', role: 'option',
        onClick: () => go(`/devices/${d.id}`),
      },
        el('span', { class: 'search-result-icon' }, Icons.laptop()),
        el('span', { class: 'search-result-main' }, d.name || d.sn || d.flexiKod || d.id),
        el('span', { class: 'search-result-sub' }, cat,
          d.sn ? el('span', null, ' · ', el('span', { class: 'sn' }, d.sn)) : null),
      ));
    }
  }
  // Registry matches land last — present but never competing with live gear.
  if (decomHint) host.appendChild(decomHint);
}

function renderProfile() {
  const user = getCurrentUser();
  // Firebase's top-level photoURL can go stale and resolve to Google's default
  // placeholder (the blue silhouette), while the per-provider photoURL stays
  // current — that's the real photo. Prefer the provider one; fall back to the
  // top-level, then to initials. Square, like every avatar in the standard.
  const photoURL = (user?.providerData || []).find((p) => p?.photoURL)?.photoURL || user?.photoURL || '';
  const name     = user?.displayName || user?.email || 'Unknown';
  const face     = avatar({ name: user?.displayName || '', email: user?.email || '', photoURL }, 'sm');

  const menuChildren = [
    el('div', {
      class: 'label profile-menu-email',
    }, user?.email || ''),
  ];

  // Admin-only: test-mode checkbox (see toggleTestMode). Real isAdmin() on
  // purpose: the escape hatch must stay visible even while view-as
  // simulates a non-admin.
  if (isAdmin()) {
    const checkbox = el('input', { type: 'checkbox' });
    checkbox.checked = isTestMode();
    const row = el('label', { class: 'profile-menu-check', role: 'menuitemcheckbox' },
      checkbox, el('span', null, 'Test mode'));
    row.addEventListener('click', async (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      checkbox.disabled = true;
      try {
        await toggleTestMode();
      } catch (err) {
        toast(err.message || 'Could not enter test mode.', 'error');
        checkbox.disabled = false;
      }
    });
    menuChildren.push(row);
  }

  menuChildren.push(el('button', { role: 'menuitem', onClick: signOutHere }, 'Sign out'));

  const menu = el('div', { class: 'profile-menu', role: 'menu' }, ...menuChildren);

  const button = el('button', {
    class: 'profile',
    'aria-haspopup': 'menu',
    'aria-expanded': 'false',
    onClick: (ev) => {
      ev.stopPropagation();
      const open = menu.classList.toggle('open');
      button.setAttribute('aria-expanded', String(open));
    },
  }, face, el('span', { class: 'name' }, name), menu);

  // Same self-cleanup as the search overlay's click-away handler.
  const onDocClickProfile = () => {
    if (!button.isConnected) { document.removeEventListener('click', onDocClickProfile); return; }
    menu.classList.remove('open');
    button.setAttribute('aria-expanded', 'false');
  };
  document.addEventListener('click', onDocClickProfile);

  return button;
}

/** Sections that get a slot in the phone bottom bar; the rest live behind More. */
const MOBILE_TABS = ['people', 'onboarding', 'offboarding', 'devices'];

/**
 * Mobile bottom tab bar — rendered into a separate host. Admin only: four
 * labelled sections plus More (a sheet with the remaining ones). Employees
 * have a single page and no bar. Sets `body.has-tabbar` so the layout can
 * reserve space for it and float toasts above it.
 */
export function renderTabBar(host) {
  clear(host);
  const adminMode = isAdmin() && isViewingAsAdmin();
  document.body.classList.toggle('has-tabbar', adminMode);
  if (!adminMode) return; // employees (and view-as User/Office): no bottom bar

  const tabs    = tabsFor(adminMode);
  const primary = tabs.filter((t) => MOBILE_TABS.includes(t.id));
  const rest    = tabs.filter((t) => !MOBILE_TABS.includes(t.id));
  const restIds = new Set(rest.map((t) => t.id));

  const item = (t) => el('a', { href: `#/${t.id}`, 'data-tab': t.id, 'aria-label': t.label },
    t.icon(), el('span', { class: 'tabbar-label' }, t.label));
  const moreBtn = el('button', {
    type: 'button', 'data-tab': 'more', 'aria-haspopup': 'dialog', 'aria-label': 'More sections',
    onClick: (e) => { e.stopPropagation(); openMoreSheet(rest); },
  }, Icons.more(), el('span', { class: 'tabbar-label' }, 'More'));

  const bar = el('nav', { class: 'tabbar', 'aria-label': 'Sections' }, ...primary.map(item), moreBtn);
  host.appendChild(bar);

  const paint = (r) => {
    for (const a of bar.querySelectorAll('[data-tab]')) {
      const id = a.dataset.tab;
      a.classList.toggle('active', id === 'more' ? restIds.has(r.section) : r.section === id);
    }
  };
  paint(getRoute());
  const unsub = onRoute((r) => {
    if (!bar.isConnected) { unsub(); return; }
    paint(r);
  });
}

// Bottom sheet with the sections that didn't fit in the bar. Closes on pick,
// backdrop tap or Escape.
function openMoreSheet(sections) {
  const route = getRoute();
  const backdrop = el('div', { class: 'sheet-backdrop' });
  const onKey = (e) => { if (e.key === 'Escape') close(); };
  const close = () => { document.removeEventListener('keydown', onKey); backdrop.remove(); };
  const sheet = el('nav', { class: 'sheet', role: 'dialog', 'aria-label': 'More sections' },
    ...sections.map((t) => el('a', {
      href: `#/${t.id}`,
      class: route.section === t.id ? 'active' : null,
      onClick: close,
    }, t.icon(), t.label)),
  );
  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });
  document.addEventListener('keydown', onKey);
  backdrop.appendChild(sheet);
  document.body.appendChild(backdrop);
}
